import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../auth/firebase';
import { useFirebaseAuth } from '../auth/FirebaseAuthContext';
import {
  AuthLayout,
  AuthError,
  authInputClass,
  authButtonClass,
} from '../components/auth/AuthLayout';
import { SocialAuthButtons } from '../components/auth/SocialAuthButtons';
import { PasswordInput } from '../components/auth/PasswordInput';
import { extractFirebaseAuthError } from '../auth/firebaseErrors';
import { usePostAuthProvision } from '../auth/usePostAuthProvision';
import { takeSignOutNotice } from '../auth/sessionExpiry';
import { captureFunnelEvent } from '../services/telemetry';

/** Toggles the one-time "set a new password" banner for migrated legacy
 * email/password users. Flip to false (or remove) once the cutover window has
 * passed. */
const SHOW_MIGRATION_NOTICE = true;

export function SignInPage() {
  const { user } = useFirebaseAuth();
  const provisionAfterAuth = usePostAuthProvision();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Lazy init also clears storage; idempotent under StrictMode remounts.
  const [signOutNotice, setSignOutNotice] = useState<string | null>(
    takeSignOutNotice
  );

  useEffect(() => {
    document.title = 'Sign In - PineappleMusicLab';
  }, []);

  // Only a verified user belongs in the workspace. An unverified user (e.g.
  // signed up moments ago, since Firebase signs them in immediately) is sent to
  // the verify-email screen instead. Anonymous guests are signed in but haven't
  // signed in with a real account — let them reach the sign-in form.
  // Refresh the ID token before provision so the backend sees email_verified.
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    if (!user.emailVerified) {
      navigate('/sign-up', { replace: true });
      return;
    }
    void (async () => {
      await auth.currentUser?.getIdToken(true);
      await provisionAfterAuth({ email: user.email ?? undefined });
      navigate('/workspace', { replace: true });
    })();
  }, [user, navigate, provisionAfterAuth]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    // The explanation has been read by the time they submit; keep it from
    // sitting above a sign-in failure and reading like part of it.
    setSignOutNotice(null);
    setSubmitting(true);
    // Capture guest state BEFORE sign-in swaps `currentUser` to the existing
    // account. A guest who signs into an existing account abandons their anon
    // UID (the project doesn't carry over), so we tag the still-anonymous
    // PostHog person to exclude them from the guest→signup funnel.
    const wasGuest = !!auth.currentUser?.isAnonymous;
    const guestUid = auth.currentUser?.uid;
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);

      // Emit while PostHog is still identified as the anon UID (before
      // provisionAfterAuth / the NavBar effect re-identify to the real account).
      if (wasGuest) {
        captureFunnelEvent('guest_signed_into_existing_account', {
          method: 'password',
          anon_uid: guestUid,
        });
      }

      // Unverified accounts are not provisioned yet — send them to the
      // verify-email screen. Stripe is created only after emailVerified.
      if (!cred.user.emailVerified) {
        navigate('/sign-up', { replace: true });
        return;
      }

      // Idempotent backend provisioning + analytics identify. Never throws.
      await provisionAfterAuth({ email });

      navigate('/workspace', { replace: true });
    } catch (err) {
      setError(extractFirebaseAuthError(err, 'Invalid email or password.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Welcome back" showLogo={false}>
      {signOutNotice && (
        <p
          className={`
            mb-5 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2
            text-sm text-gray-700
          `}
          role="status"
        >
          {signOutNotice}
        </p>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <input
          className={authInputClass}
          type="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <PasswordInput
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button className={authButtonClass} type="submit" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div className="mt-2.5 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Don&apos;t have an account?{' '}
          <Link to="/sign-up" className="text-yellow-600 underline">
            Sign up
          </Link>
        </p>
        <Link
          to="/forgot-password"
          className="text-sm text-yellow-600 underline"
        >
          Forgot password?
        </Link>
      </div>

      <AuthError message={error} />

      <SocialAuthButtons onError={setError} />

      {SHOW_MIGRATION_NOTICE && (
        <div
          className={`
            mt-5 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2
            text-sm text-gray-700
          `}
        >
          As part of a security upgrade, accounts created with{' '}
          <strong>Email &amp; password</strong> on or before{' '}
          <strong>June 22, 2026</strong> need to set a new password — just click{' '}
          <Link to="/forgot-password" className="text-yellow-600 underline">
            Reset password
          </Link>{' '}
          . If you sign in with <strong>Google</strong>, continue as usual — no
          action needed. Questions? Email us at{' '}
          <a
            href="mailto:hello@pineapplemusiclab.com"
            className="text-yellow-600 underline"
          >
            hello@pineapplemusiclab.com
          </a>
          .
        </div>
      )}
    </AuthLayout>
  );
}
